import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { getCacheStats, getStorageQuota } from '../cache';
import type { CacheStats, StorageQuota } from '../cache';
import { redactToken } from '../storage';
import { APP_VERSION } from '../config';
import type { Manifest } from '../types';
import type { HydrationProgress } from '../hydrate';

interface DebugStats {
  lastSyncAt: number | null;
  lastSyncResult: 'OK' | '304' | 'ERROR' | null;
  lastSyncError: string | null;
  errorCount: number;
  hydrationProgress: HydrationProgress | null;
  lastHeartbeatAt: number | null;
  lastHeartbeatResult: 'OK' | 'ERROR' | null;
}

export interface DebugOverlayProps {
  visible: boolean;
  token: string;
  activeManifest: Manifest | null;
  currentIndex: number;
  hasPendingSwap: boolean;
  isHydrating: boolean;
  stats: DebugStats;
}

export function DebugOverlay(props: DebugOverlayProps) {
  const { visible, token, activeManifest, currentIndex, hasPendingSwap, isHydrating, stats } = props;
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  const [cache, setCache] = useState<CacheStats>({ count: 0, totalBytes: 0 });
  const [quota, setQuota] = useState<StorageQuota>({ usage: null, quota: null, available: null });

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const refresh = async (): Promise<void> => {
      const [c, q] = await Promise.all([getCacheStats(), getStorageQuota()]);
      if (!cancelled) { setCache(c); setQuota(q); }
    };
    void refresh();
    const id = window.setInterval(() => void refresh(), 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [visible]);

  if (!visible) return null;

  const items = activeManifest?.playlist.items.length ?? 0;
  const rows: Array<[string, string]> = [
    ['Screen', activeManifest?.screen.name ?? '—'],
    ['Screen ID', activeManifest ? short(activeManifest.screen.id) : '—'],
    ['App version', APP_VERSION],
    ['Online', online ? 'yes' : 'NO (cached)'],
    ['Manifest hash', activeManifest ? short(activeManifest.manifest_hash) : '—'],
    ['Playlist', activeManifest?.playlist.id
      ? `${short(activeManifest.playlist.id)} v${activeManifest.playlist.version}`
      : '—'],
    ['Item', items > 0 ? `${currentIndex + 1} / ${items}` : '—'],
    ['Cache', `${cache.count} items, ${fmtBytes(cache.totalBytes)}`],
    ['Quota', quota.quota !== null
      ? `${fmtBytes(quota.usage ?? 0)} / ${fmtBytes(quota.quota)}`
      : 'unavailable'],
    ['Last sync', fmtLast(stats.lastSyncAt, stats.lastSyncResult, stats.lastSyncError)],
    ['Last heartbeat', fmtLast(stats.lastHeartbeatAt, stats.lastHeartbeatResult, null)],
    ['Hydration', isHydrating ? fmtProgress(stats.hydrationProgress) : 'idle'],
    ['Pending swap', hasPendingSwap ? 'yes (next boundary)' : 'no'],
    ['Errors', String(stats.errorCount)],
    ['Token', redactToken(token)],
  ];

  return (
    <div style={S.overlay}>
      {rows.map(([k, v]) => (
        <div key={k} style={S.row}>
          <span style={S.label}>{k}</span>
          <span style={S.value}>{v}</span>
        </div>
      ))}
      <div style={S.hint}>press 'd' to hide</div>
    </div>
  );
}

function short(s: string, n = 10): string {
  return s.length <= n + 3 ? s : `${s.slice(0, n)}…`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function fmtLast(at: number | null, result: string | null, err: string | null): string {
  if (at === null) return 'never';
  const secs = Math.floor((Date.now() - at) / 1000);
  const ago = secs < 60 ? `${secs}s ago` : `${Math.floor(secs / 60)}m ago`;
  const base = `${result ?? '?'} (${ago})`;
  return err ? `${base} — ${err}` : base;
}

function fmtProgress(p: HydrationProgress | null): string {
  if (!p) return 'starting…';
  return `${p.itemsCompleted}/${p.totalItems} (dl ${p.itemsDownloaded}, fail ${p.itemsFailed})`;
}

const S: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed', top: 12, right: 12,
    minWidth: 320, maxWidth: 400,
    background: 'rgba(0,0,0,0.85)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    padding: '12px 16px',
    fontFamily: 'ui-monospace, Menlo, monospace',
    fontSize: 12, lineHeight: 1.6, color: '#fff',
    zIndex: 1000, pointerEvents: 'none',
  },
  row: { display: 'flex', justifyContent: 'space-between', gap: 12 },
  label: { opacity: 0.5, flexShrink: 0 },
  value: {
    textAlign: 'right', overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  hint: {
    marginTop: 8, paddingTop: 8,
    borderTop: '1px solid rgba(255,255,255,0.1)',
    fontSize: 10, opacity: 0.4, textAlign: 'center',
  },
};
